"use client"

import { useState, useEffect, useCallback } from "react"
import { Upload } from "lucide-react"
import { useUploadDocument } from "@/contexts/UploadDocumentContext"
import { cn } from "src/utils/Functions"
import { AnimatePresence, motion } from "motion/react"

const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"]

function isValidFile(file: File): boolean {
  const extension = "." + file.name.split(".").pop()?.toLowerCase()
  return ACCEPTED_EXTENSIONS.includes(extension)
}

function hasValidFiles(dataTransfer: DataTransfer): boolean {
  // Check items if available (Chrome, Firefox)
  if (dataTransfer.items) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i]
      if (item.kind === "file") {
        // We can't check the filename here in some browsers,
        // so we'll just check if it's a file
        return true
      }
    }
  }
  // Fallback to files
  return dataTransfer.files.length > 0
}

export function GlobalDragDropOverlay() {
  const { setDroppedFiles, isOpen } = useUploadDocument()
  const [isDragging, setIsDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Only show overlay if dragging files (not text or other content)
    if (e.dataTransfer && hasValidFiles(e.dataTransfer)) {
      setDragCounter(prev => prev + 1)
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setDragCounter(prev => {
      const newCount = prev - 1
      if (newCount === 0) {
        setIsDragging(false)
      }
      return newCount
    })
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setIsDragging(false)
    setDragCounter(0)

    const files = Array.from(e.dataTransfer?.files || []).filter(isValidFile)
    if (files.length > 0) {
      setDroppedFiles(files)
    }
  }, [setDroppedFiles])

  useEffect(() => {
    // Don't listen for drops if dialog is already open
    if (isOpen) {
      setIsDragging(false)
      setDragCounter(0)
      return
    }

    window.addEventListener("dragenter", handleDragEnter)
    window.addEventListener("dragleave", handleDragLeave)
    window.addEventListener("dragover", handleDragOver)
    window.addEventListener("drop", handleDrop)

    return () => {
      window.removeEventListener("dragenter", handleDragEnter)
      window.removeEventListener("dragleave", handleDragLeave)
      window.removeEventListener("dragover", handleDragOver)
      window.removeEventListener("drop", handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop, isOpen])

  return (
    <AnimatePresence>
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-100 pointer-events-none"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className={cn(
                "w-full max-w-lg rounded-2xl border-2 border-dashed border-primary",
                "bg-primary/5 p-12 text-center"
              )}
            >
              <motion.div
                initial={{ y: 8 }}
                animate={{ y: 0 }}
                className="flex flex-col items-center gap-4"
              >
                <div className="rounded-full bg-primary/10 p-4">
                  <Upload className="size-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Drop to upload
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    PDF, DOC, or DOCX files
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}




